import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  Flex,
  Input,
  Label,
  PlusIcon,
  Text,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import {
  ChannelSelector,
  useChannelGroupsFilter,
} from '~publish/components/ChannelSelector'
import type { NormalizedChannel } from '~publish/components/ChannelSelector'
import { useOrganizationId } from '~publish/contexts/AccountContext'

import { useAssignableChannels } from '../../hooks'
import type { CollaborationRole } from '../../utils/collaborationRoles'

import styles from './ChannelAccessField.module.css'

const CONTROL_ID = 'invite-channels'

const formatChannelCount = (count: number): string =>
  count === 1 ? '1 Channel' : `${count} Channels`

interface ChannelAccessSelectorProps {
  channels: NormalizedChannel[]
  value: string[]
  onChange: (channelIds: string[]) => void
}

const ChannelAccessSelector = ({
  channels,
  value,
  onChange,
}: ChannelAccessSelectorProps): JSX.Element => {
  const [open, setOpen] = useState(false)

  const {
    filteredChannelGroups,
    selectedChannelGroupIds,
    handleSelectGroup,
    isExpanded,
    setIsExpanded,
  } = useChannelGroupsFilter({
    selectedChannelIds: value,
    onChannelSelect: onChange,
    open,
  })

  const handleSelectAll = (): void => {
    onChange(value.length > 0 ? [] : channels.map((channel) => channel.id))
  }

  return (
    <ChannelSelector
      value={value}
      onChange={onChange}
      open={open}
      onOpenChange={setOpen}
    >
      <ChannelSelector.Trigger>
        <Button
          id={CONTROL_ID}
          variant="secondary"
          size="large"
          className={styles.control}
        >
          {value.length === 0
            ? 'Select channels'
            : formatChannelCount(value.length)}
          <ChevronDownIcon />
        </Button>
      </ChannelSelector.Trigger>

      <ChannelSelector.Content
        className={styles.content}
        align="end"
        aria-label="Channel access"
        // Each pick re-renders the dialog behind the popover; keep it open
        // for multi-select. Pointer-outside and Escape still close it.
        onFocusOutside={(event): void => event.preventDefault()}
      >
        <ChannelSelector.Search placeholder="Search channels" />
        <ChannelSelector.List>
          {filteredChannelGroups.length > 0 && (
            <ChannelSelector.GroupsOverflow
              groups={filteredChannelGroups}
              selectedIds={selectedChannelGroupIds}
              onSelectGroup={handleSelectGroup}
              isExpanded={isExpanded}
              onExpand={(): void => setIsExpanded(true)}
            >
              <ChannelSelector.NewGroupLink>
                <PlusIcon size={16} />
                Group
              </ChannelSelector.NewGroupLink>
            </ChannelSelector.GroupsOverflow>
          )}

          <Flex justify="between" align="center" className={styles.selectAll}>
            <Text>Channels</Text>
            <UnstyledButton onClick={handleSelectAll}>
              <Text>{value.length > 0 ? 'Deselect all' : 'Select all'}</Text>
            </UnstyledButton>
          </Flex>

          <ChannelSelector.Empty />
          {channels.map((channel) => (
            <ChannelSelector.Item
              key={channel.id}
              value={channel.id}
              channel={channel}
              keywords={[channel.name, channel.service]}
            />
          ))}
        </ChannelSelector.List>
      </ChannelSelector.Content>
    </ChannelSelector>
  )
}

interface ChannelAccessFieldProps {
  role: CollaborationRole
  value: string[]
  onChange: (channelIds: string[]) => void
}

/**
 * The Channel Access row of the Roles invite dialog. Admins get every channel
 * on acceptance (CORE-8045), so for them the control is a read-only
 * "All N Channels" (focusable and announced, just not editable); other roles
 * pick channels from the organization's assignable channels.
 */
export const ChannelAccessField = ({
  role,
  value,
  onChange,
}: ChannelAccessFieldProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const { assignableChannels } = useAssignableChannels({
    organizationId: organizationId ?? '',
  })

  const channels = useMemo<NormalizedChannel[]>(
    () =>
      (assignableChannels ?? []).map(({ channel }) => ({
        id: channel.id,
        name: channel.name,
        service: channel.service,
        avatar: channel.avatar || null,
      })),
    [assignableChannels],
  )

  const isAdmin = role === 'admin'

  return (
    <Flex align="center" justify="between" gap="md" fullWidth>
      <Flex
        direction="column"
        gap="3xs"
        className={isAdmin ? styles.lockedDescription : undefined}
      >
        <Label htmlFor={CONTROL_ID}>Channel Access</Label>
        <Text color="subtle" size="sm">
          {isAdmin
            ? 'Admins have access to all channels'
            : 'Which channels they can access'}
        </Text>
      </Flex>
      {isAdmin ? (
        <Input
          id={CONTROL_ID}
          size="large"
          readOnly
          value={
            channels.length > 0
              ? `All ${formatChannelCount(channels.length)}`
              : 'All channels'
          }
          className={styles.readOnly}
        />
      ) : (
        <ChannelAccessSelector
          channels={channels}
          value={value}
          onChange={onChange}
        />
      )}
    </Flex>
  )
}
